/**
 * @bos/portal3 — THE SUBJECT STRIP: what a question is about, above the question.
 *
 * The owner's complaint that started this: being asked to decide something with nothing on
 * screen to decide it against. Routing questions into their rooms (questionRouting.ts) fixes
 * the biggest half — the artifact is now beside the card. This strip fixes the other half, in
 * the owner's own words rather than the machine's:
 *
 *   0. WHAT IT NAMES — when the question carries the finding's own structured `refs` (phase 2),
 *      say the thing it is about: "About: Reservation.status". This is the ONE anchor that is
 *      not a guess: the emitter declared it, the gate carried it, the contract kept it. It rides
 *      above the claim quote because it is the harder fact.
 *   1. THE CLAIM, VERBATIM — when the question joins to a claim the intake pulled out of the
 *      brief, quote the brief. "From your brief: …". This is the "show me the conflicting
 *      sentence" the owner asked for, and it already exists on disk.
 *   2. THE HONEST ABSENCE — when the artifact the question is about has not been derived yet,
 *      say exactly that and show what the machine IS working from: the claims of the kinds that
 *      feed this room. Never a half-drawn model; the chain does not checkpoint partial
 *      derivations, so there is nothing partial that could honestly be shown.
 *   3. NOTHING AT ALL — when the run carries no grounded brief (every build made before the
 *      intake leg existed, which is both of today's), no refs, or the artifact is right there on
 *      the page and no claim joins. An apologetic "no context available" box would be filler:
 *      worse than the silence it replaces, and on screen forever for every old run.
 *
 * THE MATCH IS EXACT, ON PURPOSE. See `claimForQuestion` — a fuzzy join here would quote the
 * owner's brief back at them beside the wrong question, which is a lie with their own words in
 * it. No quote is the honest failure mode, and it is a quiet one. The same rule governs `refs`:
 * only axes that name something an owner recognises are rendered, and nothing is inferred.
 */
import type { CheckpointQuestion, RunIntakeSurface } from '@bos/portal3-server';
import { claimForQuestion, claimQuote, claimsFeedingRoom, hasClaims } from './questionRouting';

export interface SubjectStripProps {
  readonly q: CheckpointQuestion;
  /** The run's grounded brief, when it has one. A pre-intake run passes undefined. */
  readonly intake: RunIntakeSurface | undefined;
  /** The room this card is standing in (its slug), or null on the Overview. */
  readonly room: string | null;
  /**
   * Has the artifact this room shows actually been derived? False ⇒ the card is being read
   * mid-derivation and the fallback applies. The caller knows this (it fetched the read
   * surface); this component never guesses.
   */
  readonly artifactDerived: boolean;
}

/** How many kind-matched claims the fallback lists before it stops — a long brief must not
 *  bury the question it is supposed to be helping with. The rest are one line away, in the
 *  data model room's own "Your brief, as understood". */
const FALLBACK_CAP = 4;

/** How many named things the About line lists before it stops — a finding that refs a dozen
 *  fields is naming a class, and a wall of names helps nobody read the question under it. */
const REFS_CAP = 3;

/**
 * WHAT THE QUESTION NAMES, in the owner's reading — `Reservation.status`, `Reservation`, or
 * nothing. Deliberately narrow in this first cut: only the two axes an owner recognises on
 * sight from the rooms they have just been reading (an entity, and a field on one). The other
 * seven axes are real and are carried on the question; they get their own renderings when the
 * rooms that speak them do (a move's from/to belongs beside a lifecycle diagram, not in a
 * strip). Nothing is inferred, nothing is prettified, and no finding code ever appears.
 *
 * Pure and exported for its own test.
 */
export function refsSubject(q: CheckpointQuestion): string | null {
  const named: string[] = [];
  for (const ref of q.refs ?? []) {
    const entity = ref.entity;
    const field = ref.field;
    const said = entity !== undefined && field !== undefined ? `${entity}.${field}` : (entity ?? field);
    if (said === undefined || said === '') continue;
    if (!named.includes(said)) named.push(said);
  }
  if (named.length === 0) return null;
  const shown = named.slice(0, REFS_CAP);
  return named.length > shown.length
    ? `${shown.join(' · ')} and ${named.length - shown.length} more`
    : shown.join(' · ');
}

/** The About line, when the question names something. Rendered above every other row of the
 *  strip, and on its own when nothing else has anything to say. */
function AboutLine({ subject }: { subject: string }) {
  return (
    <>
      <span className="qstrip__label">About</span>
      <span className="qstrip__about">{subject}</span>
    </>
  );
}

export function SubjectStrip({ q, intake, room, artifactDerived }: SubjectStripProps) {
  const subject = refsSubject(q);

  if (!hasClaims(intake) || intake === undefined) {
    // No grounded brief on this run — but a question that NAMES something still says what.
    return subject === null ? null : (
      <div className="qstrip" aria-label="What this is about">
        <AboutLine subject={subject} />
      </div>
    );
  }

  const claim = claimForQuestion(intake.claims, q);
  if (claim !== null) {
    return (
      <div className="qstrip" aria-label="What this is about">
        {subject !== null && <AboutLine subject={subject} />}
        <span className="qstrip__label">From your brief</span>
        <span className="qstrip__quote">“{claimQuote(claim)}”</span>
      </div>
    );
  }

  // The artifact is on the page — the card sits beside the thing it is about, which is the
  // whole point of the room. The only thing worth adding is WHICH part of it, when the question
  // names one.
  if (artifactDerived) {
    return subject === null ? null : (
      <div className="qstrip" aria-label="What this is about">
        <AboutLine subject={subject} />
      </div>
    );
  }

  const feeding = claimsFeedingRoom(intake.claims, room);
  if (feeding.length === 0) {
    return subject === null ? null : (
      <div className="qstrip" aria-label="What this is about">
        <AboutLine subject={subject} />
      </div>
    );
  }
  const shown = feeding.slice(0, FALLBACK_CAP);

  return (
    <div className="qstrip qstrip--absent" aria-label="What this is about">
      {subject !== null && <AboutLine subject={subject} />}
      <span className="qstrip__label">There is nothing to show yet</span>
      <p className="qstrip__absent">
        This came up while that part of your app was still being worked out, so there is no
        finished version to put beside it. This is what the machine is working from:
      </p>
      <ul className="qstrip__claims">
        {shown.map((c) => (
          <li key={c.id}>“{claimQuote(c)}”</li>
        ))}
      </ul>
      {feeding.length > shown.length && (
        <p className="qstrip__more">
          …and {feeding.length - shown.length} more from your brief.
        </p>
      )}
    </div>
  );
}
